// src/constants/index.ts
// All configuration now fetched dynamically from the API.

export const API_BASE =
  process.env.REACT_APP_API_BASE || "http://localhost:8000/api/v1";
export const API_KEY = process.env.REACT_APP_API_KEY || "alpha-dhyana-2025";

export type StageMap = Record<number, string>;

// -------------------- small in-memory cache --------------------
const CACHE_TTL = 60 * 1000; // 60 seconds
const cache = new Map<string, { data: any; timestamp: number }>();

function getCached<T = any>(key: string): T | null {
  const cached = cache.get(key);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    return cached.data as T;
  }
  cache.delete(key);
  return null;
}

function setCache<T = any>(key: string, data: T): T {
  cache.set(key, { data, timestamp: Date.now() });
  return data;
}

// -------------------- types & normalizers --------------------
export type Persona = {
  key: string;
  name: string;
  complexity_preference: string;
  authority_preference: string;
};

export type Stage = { number: number; name: string; type: string };

function normalizePersonas(data: any): Persona[] {
  if (Array.isArray(data)) {
    return data.map((p) => ({
      key: p.key || p.persona_key,
      name: p.name,
      complexity_preference: p.complexity_preference || "balanced",
      authority_preference: p.authority_preference || "medium",
    }));
  }
  return Object.entries(data).map(([key, value]: [string, any]) => ({
    key,
    name: value.name || key,
    complexity_preference: value.complexity_preference || "balanced",
    authority_preference: value.authority_preference || "medium",
  }));
}

function normalizeStages(data: any): Stage[] {
  if (Array.isArray(data)) {
    return data.map((s) => ({
      number: s.number || s.stage,
      name: s.name,
      type: s.type || "discovery",
    }));
  }
  return Object.entries(data).map(([num, value]: [string, any]) => ({
    number: parseInt(num, 10),
    name: value.name || `Stage ${num}`,
    type: value.type || "discovery",
  }));
}

// -------------------- API helpers --------------------
export async function fetchPersonas(): Promise<Persona[]> {
  const cached = getCached<Persona[]>("personas");
  if (cached) return cached;

  const res = await fetch(`${API_BASE}/config/personas?api_key=${API_KEY}`);
  if (!res.ok) throw new Error("Failed to fetch personas");
  const data = await res.json();

  return setCache("personas", normalizePersonas(data));
}

export async function fetchStages(): Promise<Stage[]> {
  const cached = getCached<Stage[]>("stages");
  if (cached) return cached;

  const res = await fetch(`${API_BASE}/config/stages?api_key=${API_KEY}`);
  if (!res.ok) throw new Error("Failed to fetch stages");
  const data = await res.json();

  return setCache("stages", normalizeStages(data));
}

/**
 * Fetches stages and returns a { [number]: name } map.
 * If a setter is provided, it will be called with that map.
 */
export async function hydrateJourneyStages(
  setStages?: (map: StageMap) => void,
): Promise<StageMap> {
  const stages = await fetchStages();
  const map: StageMap = Object.fromEntries(
    stages.map((s) => [s.number, s.name || `Stage ${s.number}`]),
  );
  if (setStages) setStages(map);
  return map;
}

// -------------------- analytics --------------------
declare global {
  interface Window {
    gtag?: (
      command: string,
      targetId: string,
      config?: Record<string, any>,
    ) => void;
  }
}

export const trackEvent = (
  eventName: string,
  properties: Record<string, any>,
) => {
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("event", eventName, properties);
  }
  if (process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.log(`Analytics Event: ${eventName}`, properties);
  }
};
